import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type {
  AdminVoucherDetail,
  IssueVoucher,
  PagedAdminVouchers,
  SaveVoucherProduct,
  VoucherProduct,
  VoucherReport,
  VoucherSearchModel,
} from './types';

/**
 * The gift-voucher catalogue, from the booking service's v2 admin API.
 *
 * Always fetched with inactive products included: the page filters on status
 * itself, and a product taken off sale is exactly the one an admin comes here
 * to put back.
 */

const BASE = 'v2/admin/vouchers';

export function useVoucherProducts() {
  return useQuery({
    queryKey: ['vouchers', 'products'],
    queryFn: () =>
      unwrap<VoucherProduct[]>(
        bookingApi.get<ApiEnvelope<VoucherProduct[]>>(
          `${BASE}/products?includeInactive=true`
        )
      ),
    staleTime: 60_000,
  });
}

function useInvalidateVouchers() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['vouchers'] });
}

/** `PUT` when the product already exists, `POST` when it does not. */
export function useSaveVoucherProduct() {
  const invalidate = useInvalidateVouchers();

  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: SaveVoucherProduct }) =>
      id
        ? unwrap<VoucherProduct>(bookingApi.put(`${BASE}/products/${id}`, body))
        : unwrap<VoucherProduct>(bookingApi.post(`${BASE}/products`, body)),
    onSuccess: invalidate,
  });
}

/** Soft delete: vouchers already sold from it are unaffected. */
export function useDeleteVoucherProduct() {
  const invalidate = useInvalidateVouchers();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<unknown>(bookingApi.delete(`${BASE}/products/${id}`)),
    onSuccess: invalidate,
  });
}

// ------------------------------------------------------------- management

export function useVoucherSearch(model: VoucherSearchModel) {
  return useQuery({
    queryKey: ['vouchers', 'search', model],
    queryFn: () =>
      unwrap<PagedAdminVouchers>(bookingApi.post(`${BASE}/search`, model)),
    placeholderData: keepPreviousData,
  });
}

export function useVoucherDetail(id: string | null) {
  return useQuery({
    queryKey: ['vouchers', 'detail', id],
    queryFn: () =>
      unwrap<AdminVoucherDetail>(
        bookingApi.get<ApiEnvelope<AdminVoucherDetail>>(`${BASE}/${id}`)
      ),
    enabled: Boolean(id),
  });
}

export function useIssueVoucher() {
  const invalidate = useInvalidateVouchers();
  return useMutation({
    mutationFn: (body: IssueVoucher) =>
      unwrap<AdminVoucherDetail>(bookingApi.post(`${BASE}/issue`, body)),
    onSuccess: invalidate,
  });
}

/**
 * The per-voucher actions. Every one of them answers with the updated detail
 * (resend with a message only), and every one refreshes the list, since status,
 * holder and expiry all show there.
 */
export function useVoucherAction<TBody>(
  voucherId: string | null,
  action: 'email' | 'reassign' | 'extend' | 'void' | 'refund' | 'resend'
) {
  const invalidate = useInvalidateVouchers();
  return useMutation({
    mutationFn: (body: TBody) => {
      const url = `${BASE}/${voucherId}/${action}`;
      return unwrap<AdminVoucherDetail | null>(
        action === 'email' ? bookingApi.put(url, body) : bookingApi.post(url, body)
      );
    },
    // Settled, not success: a refund that fails part-way has still refunded
    // part of the money, and the detail must show what actually happened.
    onSettled: invalidate,
  });
}

// ------------------------------------------------------------------ report

/** `from`/`to` are API instants — see `toApiDate`. */
export function useVoucherReport(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['vouchers', 'report', from, to],
    queryFn: () =>
      unwrap<VoucherReport>(
        bookingApi.get<ApiEnvelope<VoucherReport>>(
          `${BASE}/report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        )
      ),
    enabled,
    placeholderData: keepPreviousData,
  });
}
